import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Activity, Brain, BarChart3, FileText, HelpCircle, Star, Link2, Send,
  MessageCircle, TrendingUp, Target, Zap, ChevronRight, Rocket,
} from "lucide-react";

interface Props { clientId: string | null }

const GREEN = "#00FF88";
const PURPLE = "#8B5CF6";
const AMBER = "#F59E0B";

const CARD_HEIGHT = 460;

const cardBase: React.CSSProperties = {
  background: "#0A0E18",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 18,
  padding: 20,
  display: "flex",
  flexDirection: "column",
  height: CARD_HEIGHT,
  overflow: "hidden",
};

const rowBg = "rgba(255,255,255,0.02)";

const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Yesterday";
  return `${d} days ago`;
};

const titleForLog = (l: any): { title: string; subtitle: string; arrow?: string; Icon: any; color: string } => {
  const t = (l.type || "").toLowerCase();
  const msg = String(l.message || "");
  if (t.includes("article") || t.includes("daily_article") || t.includes("generate_articles"))
    return { title: "Article published", subtitle: msg ? `"${msg.slice(0, 60)}"` : "—", arrow: "→ submitted to Google", Icon: FileText, color: GREEN };
  if (t.includes("question"))
    return { title: msg.match(/^\d+/) ? `${msg}` : "Questions captured", subtitle: msg && !msg.match(/^\d+/) ? msg : "Latest scan", Icon: HelpCircle, color: PURPLE };
  if (t.includes("entity"))
    return { title: "Entity optimized", subtitle: msg || "Signals added", Icon: Star, color: GREEN };
  if (t.includes("citation"))
    return { title: "Citation page created", subtitle: msg || "—", Icon: Link2, color: PURPLE };
  if (t.includes("indexnow") || t.includes("bing"))
    return { title: "Bing IndexNow", subtitle: msg || "Pages submitted", Icon: Send, color: PURPLE };
  if (t.includes("index"))
    return { title: "Indexing run", subtitle: msg || "Submitted to Google", Icon: Send, color: PURPLE };
  if (t.includes("answer"))
    return { title: "Answer page created", subtitle: msg || "—", Icon: MessageCircle, color: PURPLE };
  if (t.includes("rank"))
    return { title: "Ranking update", subtitle: msg || "Positions refreshed", Icon: BarChart3, color: GREEN };
  return { title: l.type || "Activity", subtitle: msg, Icon: Activity, color: GREEN };
};

const MiniBars = ({ data, color, invert = false }: { data: number[]; color: string; invert?: boolean }) => {
  const max = Math.max(1, ...data);
  return (
    <div className="flex h-[26px] items-end gap-[3px]">
      {data.map((v, i) => {
        const h = invert ? (v ? Math.max(15, 100 - (v / max) * 100) : 8) : (v / max) * 100;
        return (
          <div
            key={i}
            style={{
              width: 5,
              height: `${Math.max(8, h)}%`,
              background: color,
              opacity: 0.4 + (i / data.length) * 0.6,
              borderRadius: 1.5,
            }}
          />
        );
      })}
    </div>
  );
};

export default function DashboardBottomSection({ clientId }: Props) {
  const isMobile = useIsMobile();
  const [logs, setLogs] = useState<any[]>([]);
  const [todayMove, setTodayMove] = useState<any | null>(null);
  const [moveGroup, setMoveGroup] = useState<any | null>(null);
  const [topQuestion, setTopQuestion] = useState<{ q: any; cov: any } | null>(null);
  const [latestPost, setLatestPost] = useState<any | null>(null);
  const [latestPostGroup, setLatestPostGroup] = useState<any | null>(null);
  const [recentNewPagesCount, setRecentNewPagesCount] = useState(0);

  const [posts, setPosts] = useState<any[]>([]);
  const [coverage, setCoverage] = useState<any[]>([]);
  const [answerPages, setAnswerPages] = useState<any[]>([]);
  const [ranks, setRanks] = useState<any[]>([]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400_000).toISOString();
      const oneDayAgo = new Date(Date.now() - 86400_000).toISOString();
      const today = new Date().toISOString().slice(0, 10);

      const [logsR, postsR, covR, apR, ranksR, moveR] = await Promise.all([
        supabase.from("conductor_logs")
          .select("id,type,message,status,created_at")
          .eq("client_id", clientId)
          .gte("created_at", sevenDaysAgo)
          .order("created_at", { ascending: false })
          .limit(50),
        supabase.from("blog_posts")
          .select("id,title,keyword,created_at,indexed_google,indexed_google_at")
          .eq("client_id", clientId)
          .order("created_at", { ascending: false }),
        supabase.from("ai_question_coverage")
          .select("id,question_id,covered,gap_priority,updated_at")
          .eq("client_id", clientId),
        supabase.from("ai_answer_pages")
          .select("id,created_at,entity_mentions,indexed_google,indexed_bing")
          .eq("client_id", clientId),
        supabase.from("rank_tracking")
          .select("rank,source,checked_at")
          .eq("client_id", clientId)
          .gte("checked_at", new Date(Date.now() - 14 * 86400_000).toISOString())
          .order("checked_at", { ascending: true }),
        supabase.from("daily_moves")
          .select("*")
          .eq("client_id", clientId)
          .eq("move_date", today)
          .order("executed_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      setLogs(logsR.data || []);
      setPosts(postsR.data || []);
      setCoverage(covR.data || []);
      setAnswerPages(apR.data || []);
      setRanks(ranksR.data || []);

      const move = moveR.data;
      setTodayMove(move);
      if (move?.normalized_key) {
        const { data: kg } = await supabase
          .from("keyword_groups")
          .select("canonical_keyword,best_position,total_impressions,normalized_key")
          .eq("client_id", clientId)
          .eq("normalized_key", move.normalized_key)
          .maybeSingle();
        if (!cancelled) setMoveGroup(kg);
      } else {
        setMoveGroup(null);
      }

      const uncoveredIds = (covR.data || [])
        .filter((c) => !c.covered)
        .sort((a, b) => (b.gap_priority || 0) - (a.gap_priority || 0));
      if (uncoveredIds.length > 0) {
        const cov = uncoveredIds[0];
        const { data: q } = await supabase
          .from("ai_questions")
          .select("id,question_text,keyword,intent_category")
          .eq("id", cov.question_id)
          .maybeSingle();
        if (!cancelled && q) setTopQuestion({ q, cov });
      }

      const lp = (postsR.data || [])[0];
      if (lp) {
        setLatestPost(lp);
        if (lp.keyword) {
          const norm = String(lp.keyword).toLowerCase().trim();
          const { data: lpg } = await supabase
            .from("keyword_groups")
            .select("total_impressions,canonical_keyword,best_position")
            .eq("client_id", clientId)
            .eq("normalized_key", norm)
            .maybeSingle();
          if (!cancelled) setLatestPostGroup(lpg);
        }
      }

      const [bp24, ap24, cp24] = await Promise.all([
        supabase.from("blog_posts").select("id", { count: "exact", head: true })
          .eq("client_id", clientId).gte("created_at", oneDayAgo),
        supabase.from("ai_answer_pages").select("id", { count: "exact", head: true })
          .eq("client_id", clientId).gte("created_at", oneDayAgo),
        supabase.from("ai_citation_pages").select("id", { count: "exact", head: true })
          .eq("client_id", clientId).gte("created_at", oneDayAgo),
      ]);
      if (!cancelled) setRecentNewPagesCount((bp24.count || 0) + (ap24.count || 0) + (cp24.count || 0));
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const last7Days = useMemo(() => {
    const days: { day: string; ts: number }[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      days.push({ day: d.toISOString().slice(0, 10), ts: d.getTime() });
    }
    return days;
  }, []);

  const postsPerDay = useMemo(() => last7Days.map(({ ts }) =>
    posts.filter((p) => {
      const t = new Date(p.created_at).getTime();
      return t >= ts && t < ts + 86400_000;
    }).length
  ), [posts, last7Days]);

  const indexedPerDay = useMemo(() => last7Days.map(({ ts }) =>
    posts.filter((p) => {
      if (!(p.indexed_google)) return false;
      const refIso = p.indexed_google_at || p.created_at;
      const t = new Date(refIso).getTime();
      return t >= ts && t < ts + 86400_000;
    }).length
  ), [posts, last7Days]);

  const capturedCumulative = useMemo(() => {
    return last7Days.map(({ ts }) =>
      coverage.filter((c) => c.covered && new Date(c.updated_at).getTime() <= ts + 86400_000).length
    );
  }, [coverage, last7Days]);

  const signalsPerDay = useMemo(() => last7Days.map(({ ts }) =>
    answerPages.filter((p) => {
      const t = new Date(p.created_at).getTime();
      return t >= ts && t < ts + 86400_000;
    }).reduce((sum, p) => {
      const em = p.entity_mentions || {};
      return sum + Object.values(em).reduce((a: number, v: any) => a + (typeof v === "number" ? v : 0), 0);
    }, 0)
  ), [answerPages, last7Days]);

  const avgRankPerDay = useMemo(() => last7Days.map(({ ts }) => {
    const dayRanks = ranks.filter((r) => {
      if (r.source !== "gsc") return false;
      const t = new Date(r.checked_at).getTime();
      return t >= ts && t < ts + 86400_000 && r.rank;
    });
    if (!dayRanks.length) return 0;
    return Math.round(dayRanks.reduce((a, r) => a + r.rank, 0) / dayRanks.length);
  }), [ranks, last7Days]);

  const contentTotal = posts.length;
  const contentThisWeek = posts.filter((p) =>
    new Date(p.created_at).getTime() > Date.now() - 7 * 86400_000
  ).length;

  const capturedCount = coverage.filter((c) => c.covered).length;
  const totalQuestions = coverage.length;
  const capturedThisWeek = coverage.filter((c) =>
    c.covered && new Date(c.updated_at).getTime() > Date.now() - 7 * 86400_000
  ).length;

  const indexedCount = posts.filter((p) => p.indexed_google).length;
  const indexedToday = posts.filter((p) =>
    (p.indexed_google) &&
    new Date(p.indexed_google_at || p.created_at).getTime() > Date.now() - 86400_000
  ).length;

  const totalSignals = answerPages.reduce((sum, p) => {
    const em = p.entity_mentions || {};
    return sum + Object.values(em).reduce((a: number, v: any) => a + (typeof v === "number" ? v : 0), 0);
  }, 0);
  const signalsThisWeek = signalsPerDay.reduce((a, b) => a + b, 0);

  const last7Ranks = ranks.filter((r) => r.source === "gsc" && r.rank &&
    new Date(r.checked_at).getTime() > Date.now() - 7 * 86400_000);
  const prev7Ranks = ranks.filter((r) => r.source === "gsc" && r.rank &&
    new Date(r.checked_at).getTime() > Date.now() - 14 * 86400_000 &&
    new Date(r.checked_at).getTime() <= Date.now() - 7 * 86400_000);
  const avgLast7 = last7Ranks.length
    ? (last7Ranks.reduce((a, r) => a + r.rank, 0) / last7Ranks.length)
    : 0;
  const avgPrev7 = prev7Ranks.length
    ? (prev7Ranks.reduce((a, r) => a + r.rank, 0) / prev7Ranks.length)
    : 0;
  const rankImprovement = avgPrev7 && avgLast7 ? (avgPrev7 - avgLast7) : 0;

  return (
    <div className={`mt-[18px] grid gap-[14px] ${isMobile ? "grid-cols-1" : "grid-cols-3"}`}>
      {/* CARD 1 — Engine Activity */}
      <div style={cardBase}>
        <SectionHeader
          title="Engine Activity"
          chip="Live"
          chipColor={GREEN}
          chipPulse
          subtitle="Real-time activity from the last 7 days"
          Icon={Activity}
          iconColor={GREEN}
        />

        <div className="mt-4 flex-1 overflow-y-auto pr-1">
          {logs.length === 0 ? (
            <div className="py-10 text-center text-[12px] text-white/40">
              No activity yet — engine starting up
            </div>
          ) : (
            <div className="flex flex-col gap-[8px]">
              {logs.slice(0, 20).map((l) => {
                const { title, subtitle, arrow, Icon, color } = titleForLog(l);
                return (
                  <div
                    key={l.id}
                    className="flex items-start gap-[10px] rounded-[12px] px-[12px] py-[12px]"
                    style={{ background: rowBg, border: "1px solid rgba(255,255,255,0.04)" }}
                  >
                    <div
                      className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px]"
                      style={{ background: `${color}14`, border: `1px solid ${color}26` }}
                    >
                      <Icon size={15} style={{ color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-[6px]">
                        <span
                          className="h-[6px] w-[6px] shrink-0 rounded-full"
                          style={{ background: color, boxShadow: `0 0 6px ${color}` }}
                        />
                        <span className="truncate text-[12px] font-[700] text-white/90">
                          {title} <span className="text-white/40">—</span>
                        </span>
                      </div>
                      <div className="mt-[3px] truncate text-[11.5px] text-white/60">
                        {subtitle}
                      </div>
                      {arrow && (
                        <div className="mt-[2px] truncate text-[10.5px] text-white/40">{arrow}</div>
                      )}
                    </div>
                    <div className="shrink-0 text-[10.5px] text-white/40 whitespace-nowrap">
                      {timeAgo(l.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* CARD 2 — Smart Decisions */}
      <div style={cardBase}>
        <SectionHeader
          title="Smart Decisions"
          chip="AI"
          chipColor={PURPLE}
          subtitle="Real decisions. Real reasoning. Real results."
          Icon={Brain}
          iconColor={PURPLE}
        />

        <SmartDecisionsBody
          decisions={[
            {
              color: GREEN, Icon: Zap, label: "KEYWORD DECISION", title: "Why this keyword?",
              value: moveGroup?.canonical_keyword || todayMove?.selected_keyword || "Awaiting today's move",
              details: moveGroup
                ? `Position #${moveGroup.best_position ?? "—"} · ${moveGroup.total_impressions ?? 0} impressions · Closest to page 1`
                : "No move executed yet today",
              arrow: "→ Highest impact move available today",
            },
            {
              color: PURPLE, Icon: Target, label: "QUESTION DECISION", title: "Why this question?",
              value: topQuestion?.q?.question_text ? `"${topQuestion.q.question_text}"` : "No uncovered questions",
              details: topQuestion
                ? `From: ${topQuestion.q.keyword || "—"} · ${topQuestion.q.intent_category || "general"} intent`
                : "All known questions covered",
              arrow: "→ High priority gap — no content exists yet",
            },
            {
              color: PURPLE, Icon: FileText, label: "CONTENT DECISION", title: "Why this article?",
              value: latestPost?.title ? `"${latestPost.title}"` : "No articles yet",
              details: latestPost
                ? `Target keyword at position ${latestPostGroup?.best_position ?? "—"} · ${latestPostGroup?.total_impressions ?? 0} monthly searches`
                : "Waiting for first article",
              arrow: "→ One article can push this to top 3",
            },
            {
              color: AMBER, Icon: Rocket, label: "INDEX DECISION", title: "Why index now?",
              value: `"${recentNewPagesCount} new page${recentNewPagesCount === 1 ? "" : "s"} ready"`,
              details: "Google Indexing API + Bing IndexNow",
              arrow: "→ Fast indexing = faster rankings",
            },
          ]}
        />
      </div>

      {/* CARD 3 — Growth Signals */}
      <div style={cardBase}>
        <SectionHeader
          title="Growth Signals"
          chip="Tracking"
          chipColor={GREEN}
          chipPulse
          subtitle="Real growth. Real impact. Real time."
          Icon={BarChart3}
          iconColor={GREEN}
        />

        <div className="mt-4 flex flex-1 flex-col gap-[10px] overflow-y-auto pr-1">
          <MetricRow
            color={GREEN}
            Icon={FileText}
            label="Content Published"
            value={String(contentTotal)}
            unit="articles"
            trend={`+${contentThisWeek}`}
            trendLabel="this week"
            data={postsPerDay}
          />
          <MetricRow
            color={PURPLE}
            Icon={Target}
            label="Questions Captured"
            value={String(capturedCount)}
            unit={`of ${totalQuestions}`}
            trend={`+${capturedThisWeek}`}
            trendLabel="this week"
            data={capturedCumulative}
          />
          <MetricRow
            color={PURPLE}
            Icon={Zap}
            label="Pages Indexed"
            value={String(indexedCount)}
            unit="pages"
            trend={`+${indexedToday}`}
            trendLabel="today"
            data={indexedPerDay}
          />
          <MetricRow
            color={GREEN}
            Icon={Star}
            label="AI Signals Built"
            value={String(totalSignals)}
            unit="mentions"
            trend={`+${signalsThisWeek}`}
            trendLabel="this week"
            data={signalsPerDay}
          />
          <MetricRow
            color={AMBER}
            Icon={TrendingUp}
            label="Avg. Keyword Position"
            value={avgLast7 ? avgLast7.toFixed(1) : "—"}
            unit=""
            trend={rankImprovement > 0
              ? `↑ ${rankImprovement.toFixed(1)}`
              : rankImprovement < 0
                ? `↓ ${Math.abs(rankImprovement).toFixed(1)}`
                : "—"}
            trendLabel="this week"
            data={avgRankPerDay}
            invert
          />
        </div>

        
      </div>
    </div>
  );
}

function SectionHeader({
  title, chip, chipColor, chipPulse, subtitle, Icon, iconColor,
}: {
  title: string; chip: string; chipColor: string; chipPulse?: boolean;
  subtitle: string; Icon: any; iconColor: string;
}) {
  return (
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-[8px]">
          <h3 className="text-[16px] font-[800] tracking-tight text-white">{title}</h3>
          <span
            className="inline-flex items-center gap-[5px] rounded-full px-[8px] py-[2px] text-[10px] font-[700]"
            style={{ background: `${chipColor}14`, border: `1px solid ${chipColor}33`, color: chipColor }}
          >
            <span
              className={`h-[5px] w-[5px] rounded-full ${chipPulse ? "animate-pulse" : ""}`}
              style={{ background: chipColor }}
            />
            {chip}
          </span>
        </div>
        <p className="mt-[6px] text-[11.5px] text-white/45">{subtitle}</p>
      </div>
      <div
        className="flex h-[32px] w-[32px] items-center justify-center rounded-[9px]"
        style={{ background: `${iconColor}10`, border: `1px solid ${iconColor}26` }}
      >
        <Icon size={15} style={{ color: iconColor }} />
      </div>
    </div>
  );
}

function FooterButton({ color, label, onClick }: { color: string; label: string; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="mt-[14px] w-full rounded-[12px] py-[12px] text-[12px] font-[700] transition-colors"
      style={{
        color,
        background: `${color}0D`,
        border: `1px solid ${color}26`,
      }}
    >
      {label}
    </button>
  );
}

function DecisionCard({
  color, Icon, label, title, value, details, arrow,
}: {
  color: string; Icon: any; label: string; title: string; value: string; details: string; arrow: string;
}) {
  return (
    <div
      className="rounded-[12px] p-[13px]"
      style={{
        background: `${color}08`,
        border: `1px solid ${color}33`,
        boxShadow: `0 0 0 1px ${color}10, 0 4px 16px ${color}10`,
      }}
    >
      <div className="flex items-start gap-[10px]">
        <div
          className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[9px]"
          style={{ background: `${color}1A`, border: `1px solid ${color}40` }}
        >
          <Icon size={14} style={{ color }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-[800] uppercase tracking-[0.7px]" style={{ color }}>
            {label}
          </div>
          <div className="mt-[3px] text-[12px] font-[700] text-white/90">{title}</div>
          <div className="mt-[3px] text-[12px] font-[600] italic" style={{ color }}>{value}</div>
          <div className="mt-[3px] text-[10.5px] text-white/55">{details}</div>
          <div className="mt-[5px] text-[10.5px] font-[700]" style={{ color }}>{arrow}</div>
        </div>
        <ChevronRight size={14} className="mt-[6px] shrink-0 text-white/30" />
      </div>
    </div>
  );
}

function MetricRow({
  color, Icon, label, value, unit, trend, trendLabel, data, invert,
}: {
  color: string; Icon: any; label: string; value: string; unit: string;
  trend: string; trendLabel: string; data: number[]; invert?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-[12px] rounded-[12px] p-[12px]"
      style={{ background: rowBg, border: "1px solid rgba(255,255,255,0.04)" }}
    >
      <div
        className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[10px]"
        style={{ background: `${color}14`, border: `1px solid ${color}33` }}
      >
        <Icon size={18} style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-[600] text-white/55">{label}</div>
        <div className="mt-[2px] flex items-baseline gap-[5px]">
          <span className="text-[20px] font-[800] tracking-tight" style={{ color }}>{value}</span>
          {unit && <span className="text-[11px] font-[600] text-white/55">{unit}</span>}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-[4px]">
        <div className="text-[12px] font-[800]" style={{ color }}>{trend}</div>
        <div className="text-[9.5px] font-[600] text-white/45">{trendLabel}</div>
      </div>
      <div className="shrink-0">
        <MiniBars data={data} color={color} invert={invert} />
      </div>
    </div>
  );
}

interface DecisionItem {
  color: string; Icon: any; label: string; title: string;
  value: string; details: string; arrow: string;
}

function SmartDecisionsBody({ decisions }: { decisions: DecisionItem[] }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (decisions.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % decisions.length), 6000);
    return () => clearInterval(t);
  }, [decisions.length]);

  const active = decisions[idx];
  if (!active) return null;
  const { color, Icon, label, title, value, details, arrow } = active;

  const nextIdx = (idx + 1) % decisions.length;
  const next = decisions[nextIdx];

  return (
    <div className="mt-4 flex flex-1 flex-col overflow-hidden">
      {/* TOP — main active decision (fills available space) */}
      <div
        className="flex flex-1 flex-col rounded-[14px] p-[18px]"
        style={{
          background: `${color}10`,
          border: `1px solid ${color}40`,
          boxShadow: `0 0 0 1px ${color}10, 0 6px 24px ${color}14`,
        }}
      >
        <div className="flex items-start gap-[12px]">
          <div
            className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: `${color}1F`, border: `1px solid ${color}4D` }}
          >
            <Icon size={18} style={{ color }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] font-[800] uppercase tracking-[0.8px]" style={{ color }}>
              {label}
            </div>
            <div className="mt-[4px] text-[14px] font-[700] text-white/90">{title}</div>
          </div>
        </div>
        <div className="mt-[10px] line-clamp-3 text-[13px] font-[600] italic" style={{ color }}>
          {value}
        </div>
        <div className="mt-[6px] line-clamp-2 text-[11px] text-white/55">{details}</div>
        <div className="mt-auto pt-[10px] text-[11px] font-[700]" style={{ color }}>{arrow}</div>
      </div>

      {/* MIDDLE — pagination dots */}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex gap-[6px]">
          {decisions.map((d, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Decision ${i + 1}`}
              className="h-[6px] rounded-full transition-all"
              style={{
                width: i === idx ? 22 : 6,
                background: i === idx ? d.color : "rgba(255,255,255,0.18)",
              }}
            />
          ))}
        </div>
        <div className="text-[10px] font-[600] text-white/40">
          {idx + 1} / {decisions.length}
        </div>
      </div>

      {/* BOTTOM — next decision preview (fills empty space) */}
      {next && (
        <button
          onClick={() => setIdx(nextIdx)}
          className="mt-3 flex items-center gap-[10px] rounded-[10px] p-[10px] text-left transition-colors hover:bg-white/[0.03]"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div
            className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[8px]"
            style={{ background: `${next.color}14`, border: `1px solid ${next.color}33` }}
          >
            <next.Icon size={13} style={{ color: next.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9.5px] font-[700] uppercase tracking-[0.6px] text-white/45">
              Next decision
            </div>
            <div className="mt-[1px] truncate text-[11.5px] font-[600] text-white/80">
              {next.title}
            </div>
          </div>
          <ChevronRight size={13} className="shrink-0 text-white/30" />
        </button>
      )}
    </div>
  );
}
